import Icon from './Icon';

/**
 * A real photograph when one exists, and a designed placeholder when one does
 * not — never generic stock imagery in a slot a reader takes for a picture of
 * this specific monastery.
 *
 * The placeholder is a maroon-tint field with, when the monastery has
 * supplied one, its own seal — see the SEAL / CREST POLICY comment in
 * data/monasteries.js — or otherwise a gold lotus mark, plus the monastery's
 * own Tibetan name where it is recorded, and (in `hero`) the reason it is
 * empty. The seal never appears over a real photo; a monastery with
 * photography does not need a second image standing in for the first.
 */
const VARIANTS = {
  hero: { icon: 56, tibetan: 20, caption: true },
  card: { icon: 40, tibetan: 17, caption: false },
  thumb: { icon: 18, tibetan: 0, caption: false },
};

export default function MonasteryImage({ photo, monastery, variant = 'card', className }) {
  const spec = VARIANTS[variant] || VARIANTS.card;

  if (photo?.source) {
    return (
      <img
        src={photo.source}
        // The name alone, not "a photo of…". Screen-reader users get the same
        // description sighted users get from the caption below it.
        alt={monastery?.name ? `${monastery.name}` : ''}
        className={className}
        loading={variant === 'hero' ? 'eager' : 'lazy'}
        decoding="async"
      />
    );
  }

  return (
    <div className="m-placeholder">
      {monastery?.seal?.source ? (
        <img
          src={monastery.seal.source}
          alt=""
          className="m-placeholder__seal"
          style={{ width: spec.icon * 2, height: spec.icon * 2 }}
        />
      ) : (
        <Icon name="lotus" size={spec.icon} />
      )}
      {spec.tibetan > 0 && monastery?.tibetanName && (
        <span className="tibetan" style={{ fontSize: spec.tibetan }}>
          {monastery.tibetanName}
        </span>
      )}
      {spec.caption && (
        <span className="m-placeholder__caption">Photography pending monastery approval</span>
      )}
    </div>
  );
}
